// src/component/OnandmeSidebar.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { api } from "../util/api";
import { mailApi } from "../page/mail/api/mailApi";
import { Link as RouterLink, useLocation, useHistory } from "react-router-dom";
import {
    Avatar, Box, Button, Collapse, IconButton, InputAdornment, Stack, TextField, Tooltip, Typography, Link,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import SubTreeLine from "./common/SubTreeLine";
import ChevronRightRoundedIcon    from "@mui/icons-material/ChevronRightRounded";
import ChevronLeftRoundedIcon     from "@mui/icons-material/ChevronLeftRounded";
import ExpandLessRoundedIcon      from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRoundedIcon      from "@mui/icons-material/ExpandMoreRounded";
import CelebrationRoundedIcon           from "@mui/icons-material/CelebrationRounded";
import GavelRoundedIcon                 from "@mui/icons-material/GavelRounded";
import ArticleRoundedIcon               from "@mui/icons-material/ArticleRounded";
import ForumRoundedIcon                 from "@mui/icons-material/ForumRounded";
import DashboardCustomizeRoundedIcon    from "@mui/icons-material/DashboardCustomizeRounded";
import ApprovalRoundedIcon              from "@mui/icons-material/ApprovalRounded";
import MailRoundedIcon                  from "@mui/icons-material/MailRounded";
import ContactsRoundedIcon              from "@mui/icons-material/ContactsRounded";
import CalendarMonthRoundedIcon         from "@mui/icons-material/CalendarMonthRounded";
import EventAvailableRoundedIcon        from "@mui/icons-material/EventAvailableRounded";
import DescriptionRoundedIcon           from "@mui/icons-material/DescriptionRounded";
import DirectionsCarRoundedIcon         from "@mui/icons-material/DirectionsCarRounded";
import FeedbackRoundedIcon              from "@mui/icons-material/FeedbackRounded";
// 과업 요청서 메뉴가 잠시 주석 처리돼 있어 지금은 미사용 — 메뉴를 되살릴 때 같이 쓰이므로 남겨둔다.
// (import 까지 지우면 되살릴 때 두 군데를 고쳐야 해서, 한 곳만 풀면 되도록 이렇게 둔다)
// eslint-disable-next-line no-unused-vars
import AssignmentRoundedIcon            from "@mui/icons-material/AssignmentRounded";
import PaymentsRoundedIcon              from "@mui/icons-material/PaymentsRounded";
import Groups2RoundedIcon               from "@mui/icons-material/Groups2Rounded";
import QrCode2RoundedIcon               from "@mui/icons-material/QrCode2Rounded";
import LogoutRoundedIcon                from "@mui/icons-material/LogoutRounded";
import HomeRoundedIcon                  from "@mui/icons-material/HomeRounded";
import LockResetRoundedIcon             from "@mui/icons-material/LockResetRounded";
import SettingsRoundedIcon              from "@mui/icons-material/SettingsRounded";
import NotificationsActiveRoundedIcon   from "@mui/icons-material/NotificationsActiveRounded";
import { confirm as confirmDialog } from "../util/confirmDialog";
import { extendSession } from "../util/session";
import ActionButton from "./common/ActionButton";
import AppHeader from "./AppHeader";
import PasswordChangeModal from "../page/dashboard/PasswordChangeModal";
import { FINANCE_NAV, HR_NAV, buildModuleChildren } from "../config/moduleNav";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

// 본문(main)의 좌측 오프셋과 항상 일치해야 함 → App.js 가 이 값을 import 해서 paddingLeft 로 사용.
export const OPEN_W   = 290;
export const CLOSED_W = 56;

/* ─── 경로 일치 여부 ──────────────────────────────── */
function matched(to, pathname, exact = false, search = "") {
    if (!to) return false;
    const [targetPath, targetSearch] = to.split("?");
    if (targetSearch != null) {
        // 쿼리는 부분집합 매칭 — 메뉴가 요구하는 파라미터(section 등)만 일치하면 됨.
        // 검색 딥링크로 &tab= 이 덧붙어도 해당 섹션 메뉴의 하이라이트가 유지된다.
        if (pathname !== targetPath) return false;
        const cur = new URLSearchParams(search);
        const want = new URLSearchParams(targetSearch);
        for (const [k, v] of want.entries()) {
            if (cur.get(k) !== v) return false;
        }
        return true;
    }
    if (targetPath === "/") return pathname === "/";
    if (exact) return pathname === targetPath;
    return pathname.startsWith(targetPath);
}

/* 자손(하위 탭 등) 중 현재 경로와 일치하는 게 하나라도 있는지 — 재귀.
   부모 섹션이 "현재 선택된 탭"이 아니라 그 조상이라는 이유만으로 하이라이트되는 걸 막는 데 쓴다. */
function anyDescendantMatched(node, pathname, search) {
    if (!node.children?.length) return false;
    return node.children.some(c =>
        matched(c.to, pathname, c.exact, search) || anyDescendantMatched(c, pathname, search)
    );
}

function normalizePermCode(permission) {
    if (typeof permission === "string") return permission;
    return permission?.code || permission?.permissionCode || permission?.name || "";
}

function hasPrefix(permissions, prefix) {
    // permissions === null 은 "아직 로딩 안 됨" — 호출자가 isAdmin 으로 별도 우대 처리하므로 여기서는 false 반환.
    // (loading 중에 메뉴가 안 보이는 깜빡임은 buildMenu 가 isAdmin 도 함께 보도록 처리)
    if (!Array.isArray(permissions)) return false;
    const codes = permissions.map(normalizePermCode).filter(Boolean);
    if (codes.includes("GLOBAL_ADMIN")) return true;
    return codes.some(p => p.startsWith(prefix));
}

/* 지출요청 섹션 하위 — 부서 목록(동적)으로 펼친다.
   '전체'(dept=all) + 부서별 링크. to 는 ExpenseRequestTab 의 deptUrl 과 동일해야 하이라이트가 맞는다. */
function expenseRequestChildren(departments) {
    const base = `/finance?section=expenserequest&tab=${encodeURIComponent("지출요청")}`;
    return [
        { id: "financeexpenserequestall", label: "지출요청-전체", isTab: true, to: `${base}&dept=all` },
        ...(departments || []).map(d => ({
            id: `financeexpenserequestdept${d.deptId}`,
            label: `${d.deptName}`,
            isTab: true,
            to: `${base}&dept=${d.deptId}`,
        })),
    ];
}

/* ─── 메뉴 정의 ────────────────────────────────────── */
function buildMenu(isAdmin, permissions, departments) {
    // 관리자(GLOBAL_ADMIN) 는 모든 도메인 메뉴를 항상 본다.
    // 두 권한 API(/auth/verify_admin, /) 의 응답 타이밍이 달라
    // permissions 가 null/[] 인 동안에도 isAdmin 이 true 면 메뉴를 즉시 표시.
    const showFinance = isAdmin || hasPrefix(permissions, "FINANCE_");
    const showHr      = isAdmin || hasPrefix(permissions, "HR_");
    const showLed     = isAdmin || hasPrefix(permissions, "LED_");

    const items = [
        // 홈(대시보드) — 항상 최상단
        { id: "home", label: "홈", icon: <HomeRoundedIcon sx={{ fontSize: 18 }} />, to: "/", exact: true },

        // 비밀번호 변경 — 이동이 아니라 모달을 여는 '액션' 항목이라 to 가 없다.
        // 대시보드 ProfileCard 안에 있던 버튼이 통째로 주석 처리돼 있어 직원이 스스로 비번을
        // 바꿀 길이 아예 없었다. 어느 화면에 있든 닿는 사이드바 최상단으로 올린다.
        { id: "change_pw", type: "action", label: "비밀번호 변경",
          icon: <LockResetRoundedIcon sx={{ fontSize: 18 }} /> },

        // 사내 운영 — 협업·소통·일정 (채널/결재/메일/캘린더)
        { id: "internal_group", type: "groupLabel", label: "사내 운영" },
        { id: "channel",  label: "채널",   icon: <ForumRoundedIcon sx={{ fontSize: 18 }} />, to: "/channel" },
        // 사내 게시판 — 공지사항·문서양식·경조사·익명 등. 예전 '공지 채널'을 대체한다.
        { id: "board", label: "사내 게시판", icon: <DashboardCustomizeRoundedIcon sx={{ fontSize: 18 }} />, to: "/board" },
        {
            id: "approval", label: "결재", icon: <ApprovalRoundedIcon sx={{ fontSize: 18 }} />,
            // 메뉴 이름을 누르면 결재 화면을 처음 상태로 되돌린다 — 상세 모달이 열려 있거나
            // 목록을 한참 내려본 상태에서 다시 누를 때 «처음으로» 가 되는 게 자연스럽다.
            resetTo: "/approval",
            children: [
                { id: "appr_received", label: "받은 결재",     to: "/approval?view=received" },
                { id: "appr_sent",     label: "올린 결재",     to: "/approval?view=sent" },
                { id: "appr_viewing",  label: "열람 결재",     to: "/approval?view=viewing" },
                { id: "appr_create",   label: "결재 작성하기", to: "/approval?create=1" },
            ],
        },
        // ─── 과업 요청서 — 메뉴에서 잠시 내림 (2026-07-28) ──────────────────────────
        // 실행/영상/디자인팀에 보내는 업무 요청. 기능은 완성돼 있고 라우트(App.js 의 /work-request)와
        // 백엔드도 그대로 살아 있다 — 아직 운영에서 쓰지 않아 <b>메뉴만</b> 감춘 것이다.
        // 다시 열려면 아래 블록의 주석만 풀면 된다(다른 곳은 손댈 필요 없음).
        // 주소로는 지금도 접근된다: /work-request?tab=request
        /*
        {
            id: "workRequest", label: "과업 요청서", icon: <AssignmentRoundedIcon sx={{ fontSize: 18 }} />,
            children: [
                // 세 항목 모두 ?tab= 을 명시한다 — 쿼리 없는 링크는 matched() 가 pathname 만 보므로
                // 어느 탭에 있든 '요청 목록' 이 계속 활성으로 남는다.
                { id: "wr_list",   label: "요청 목록",   to: "/work-request?tab=request" },
                { id: "wr_manual", label: "업무 메뉴얼", to: "/work-request?tab=manual" },
                { id: "wr_format", label: "요청 양식",   to: "/work-request?tab=format" },
            ],
        },
        */
        {
            id: "mail", label: "메일", icon: <MailRoundedIcon sx={{ fontSize: 18 }} />,
            children: [
                { id: "mail_inbox",   label: "받은 메일함",   to: "/mail?folder=INBOX" },
                { id: "mail_sent",    label: "보낸 메일함",   to: "/mail?folder=SENT" },
                { id: "mail_compose", label: "메일 작성하기", to: "/mail?compose=1" },
                // 수신 그룹은 주소록이 아니라 발송 묶음이라 연락처 링크보다 작성하기 쪽에 붙인다
                // (실제로 쓰이는 자리가 '메일을 쓸 때' 라서). 메일 화면 안 사이드바에서도 도구 섹션에 있다.
                { id: "mail_groups",  label: "메일 수신 그룹", to: "/mail?groups=1" },
                // 주소록은 메일을 쓰다 찾는 경우가 대부분이라 메일 아래에 둔다.
                // 메일 화면 안 사이드바에도 같은 항목이 있고, 둘 다 같은 딥링크(?contacts=)로 들어간다.
                // 팀 연락처는 2026-08-08 폐지 — 주소록은 '나만'(개인)과 '회사가 함께'(공용) 둘로 갈린다.
                { id: "mail_contacts_personal", label: "내 연락처",     to: "/mail?contacts=PERSONAL" },
                { id: "mail_contacts_public",   label: "공용 연락처",   to: "/mail?contacts=PUBLIC" },
            ],
        },
        // 사내 연락처 — 메일과 별개인 독립 페이지(/contacts). 메일 아래의 '연락처' 링크(?contacts=)는
        // 메일 화면 안으로 들어가지만, 이 최상위 항목은 같은 ContactsPanel 을 메일 밖 전용 페이지로 연다.
        { id: "contacts", label: "사내 연락처", icon: <ContactsRoundedIcon sx={{ fontSize: 18 }} />, to: "/contacts" },
        { id: "calendar", label: "사내 일정", icon: <CalendarMonthRoundedIcon sx={{ fontSize: 18 }} />, to: "/calendar" },

        // id 는 React key 로 쓰인다 — '사내 운영'(internal_group)과 겹치면 두 그룹 라벨이 같은 key 가 돼
        // React 가 형제 구분을 못 한다("two children with the same key" 경고, 라벨 누락·뒤바뀜 가능).
        { id: "activity_group", type: "groupLabel", label: "사내 활동" },
        // 예약 — 사내에서 '미리 잡아 두는' 것들을 한 갈래로 묶는다(비품 · 회의실).
        // 예전엔 '비품 예약'만 최상위에 있었는데, 회의실이 붙으면서 성격이 같은 두 화면이
        // 서로 떨어져 있으면 찾기 어려워진다.
        {
            id: "reservation", label: "예약", icon: <EventAvailableRoundedIcon sx={{ fontSize: 18 }} />,
            children: [
                { id: "equipment",   label: "비품 예약",   to: "/equipment" },
                { id: "meetingRoom", label: "회의실 예약", to: "/meeting-room" },
            ],
        },
        { id: "meetingMinutes", label: "회의록", icon: <DescriptionRoundedIcon sx={{ fontSize: 18 }} />, to: "/meeting-minutes" },
        { id: "vehicleLog", label: "차계부", icon: <DirectionsCarRoundedIcon sx={{ fontSize: 18 }} />, to: "/vehicle-log" },
        { id: "feedback", label: "피드백", icon: <FeedbackRoundedIcon sx={{ fontSize: 18 }} />, to: "/feedback" },


        { id: "project_group", type: "groupLabel", label: "행사 운영" },
        {
            id: "event", label: "행사", icon: <CelebrationRoundedIcon sx={{ fontSize: 18 }} />,
            children: [
                {
                    id: "kv", label: "키비주얼 (2D)",
                    children: [
                        { id: "kv_list",   label: "목록",   to: "/onandme/list/2d" },
                        { id: "kv_upload", label: "업로드", to: "/onandme/upload/2d" },
                    ],
                },
                {
                    id: "d3", label: "3D",
                    children: [
                        { id: "d3_list",   label: "목록",   to: "/onandme/list/3d" },
                        { id: "d3_upload", label: "업로드", to: "/onandme/upload/3d" },
                    ],
                },
                {
                    id: "venue", label: "행사장",
                    children: [
                        { id: "venue_list",   label: "목록",   to: "/onandme/event-venue" },
                        { id: "venue_upload", label: "업로드", to: "/onandme/upload/event-venue" },
                    ],
                },
            ],
        },
        ...(showLed ? [{
            id: "led", label: "LED 포토존", icon: <QrCode2RoundedIcon sx={{ fontSize: 18 }} />,
            children: [
                { id: "led_event", label: "행사 관리", to: "/qr-event-admin-list?tab=events" },
                { id: "led_participants", label: "참가자 관리", to: "/qr-event-admin-list?tab=participants" },
                { id: "led_attendance", label: "RFID 출석체크", to: "/qr-event-participant-attendance", external: true },
            ],
        }] : []),

        // 입찰 — 나라장터 공고 + 제안서 (외부 영업/수주 관련)
        { id: "bid_group", type: "groupLabel", label: "입찰" },
        { id: "g2b", label: "나라장터 공고", icon: <GavelRoundedIcon sx={{ fontSize: 18 }} />, to: "/g2b" },
        {
            id: "proposal", label: "제안서", icon: <ArticleRoundedIcon sx={{ fontSize: 18 }} />,
            children: [
                // exact 필수: "/onandme/proposal" 는 "/onandme/proposal/upload" 의 prefix라
                // exact 없으면 업로드 페이지에서 startsWith 로 목록도 함께 활성화됨.
                { id: "prop_list",   label: "목록",   to: "/onandme/proposal", exact: true },
                { id: "prop_upload", label: "업로드", to: "/onandme/proposal/upload" },
            ],
        },

        // 관리자 전용 — 시스템 전역 설정 (GLOBAL_ADMIN 만 노출)
        ...(isAdmin ? [
            { id: "admin_group", type: "groupLabel", label: "관리자" },
            { id: "system_setting", label: "시스템 설정", icon: <SettingsRoundedIcon sx={{ fontSize: 18 }} />, to: "/admin/system-setting" },
            { id: "push_admin", label: "휴대폰 알림", icon: <NotificationsActiveRoundedIcon sx={{ fontSize: 18 }} />, to: "/admin/push" },
        ] : []),


    ];

    // 별도 운영 — 재무·인사 (권한 보유자만 표시되며, 사내 운영과 분리해 가장 아래)
    if (showFinance || showHr) {
        items.push({ id: "separate_group", type: "groupLabel", label: "별도 운영" });
    }
    // 재무·인사 하위 메뉴는 config/moduleNav.js(단일 출처)에서 섹션>탭 2계층으로 생성.
    // 이름 변경은 그 파일만 고치면 사이드바·페이지 탭·검색에 모두 반영된다.
    // canSee: 권한 있는 탭만 노출(볼 탭이 하나도 없는 섹션은 자동 숨김).
    const permSet = new Set((Array.isArray(permissions) ? permissions : []).map(normalizePermCode));
    const canSee = (perm) => isAdmin || permSet.has("GLOBAL_ADMIN") || permSet.has(perm);

    if (showFinance) {
        // 지출요청(부서) 는 정적 탭 대신 부서 목록(동적)을 하위로 펼치고,
        // 지출요청(프로젝트별) 은 반대로 자식을 지워 단일 링크로 둔다 — 프로젝트는 계속 늘어나서
        // 하위로 펼치면 사이드바가 통째로 프로젝트 목록이 된다. 찾기는 화면 안 드롭다운·검색이 맡는다.
        // (자식이 없으면 SubGroup 이 리프 링크로 그린다.)
        const children = buildModuleChildren(FINANCE_NAV, canSee).map(sec => {
            if (sec.id === "financeexpenserequest")        return { ...sec, children: expenseRequestChildren(departments) };
            if (sec.id === "financeexpenserequestproject") return { ...sec, children: [] };
            return sec;
        });
        if (children.length) items.push({
            id: "finance", label: "재무", icon: <PaymentsRoundedIcon sx={{ fontSize: 18 }} />, children,
        });
    }
    if (showHr) {
        const children = buildModuleChildren(HR_NAV, canSee);
        if (children.length) items.push({
            id: "hr", label: "인사", icon: <Groups2RoundedIcon sx={{ fontSize: 18 }} />, children,
        });
    }

    return items;
}

/* ─── 사이드바 통합 검색 ────────────────────────────
   메뉴 트리(대분류 그룹 → 중분류 → 세분류 → 소분류 → 재무/인사 섹션·탭)를 통째로 펼쳐
   한 입력으로 검색하고, 선택하면 바로 이동한다.
   재무·인사 탭은 이미 메뉴 트리에 nested 로 들어있어(config/moduleNav.js) 별도 등록이 필요 없다. */
function flattenMenu(nodes, crumbs, group, out) {
    for (const node of nodes) {
        if (node.type === "groupLabel") { group = node.label; continue; }
        const path = group ? [group, ...crumbs] : [...crumbs];
        if (node.to) {
            out.push({ label: node.label, crumbs: path, to: node.to, external: node.external, isTab: node.isTab });
        }
        if (node.children?.length) {
            flattenMenu(node.children, [...crumbs, node.label], group, out);
        }
    }
    return out;
}

function SidebarSearch({ menuTree, history }) {
    const [q, setQ] = useState("");

    // 검색 인덱스 = 권한 필터가 이미 적용된 메뉴 트리 전 계층(탭 포함)
    const index = useMemo(() => flattenMenu(menuTree, [], null, []), [menuTree]);

    const results = useMemo(() => {
        const keyword = q.trim().toLowerCase();
        if (!keyword) return [];
        return index
            .filter(e => (e.label + " " + e.crumbs.join(" ")).toLowerCase().includes(keyword))
            .slice(0, 8);
    }, [q, index]);

    const go = (entry) => {
        setQ("");
        if (entry.external) window.open(entry.to, "_blank", "noopener");
        else history.push(entry.to);
    };

    return (
        <Box sx={{ px: 1.25, pt: 1, pb: 0.5 }}>
            <TextField
                size="small" fullWidth value={q}
                onChange={e => setQ(e.target.value)}
                onKeyDown={e => {
                    if (e.key === "Enter" && results.length > 0) go(results[0]);
                    if (e.key === "Escape") setQ("");
                }}
                placeholder="메뉴·탭 검색"
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchRoundedIcon sx={{ fontSize: 16, color: "#94a3b8" }} />
                        </InputAdornment>
                    ),
                }}
                sx={{
                    "& .MuiOutlinedInput-root": {
                        borderRadius: 1, bgcolor: "#f8fafc", fontSize: 13,
                        "& fieldset": { borderColor: "rgba(15,23,42,0.08)" },
                    },
                    "& input": { py: 0.8 },
                }}
            />
            {/* 결과 목록 — 입력 바로 아래 인라인 표시, 선택 시 이동 후 닫힘 */}
            {results.length > 0 && (
                <Box sx={{
                    mt: 0.5, border: "1px solid rgba(15,23,42,0.08)",
                    bgcolor: "#fff", boxShadow: "0 4px 12px rgba(15,23,42,0.06)",
                }}>
                    {results.map((r, i) => (
                        <Box key={`${r.to}-${i}`} onClick={() => go(r)} sx={{
                            px: 1.25, py: 0.7, cursor: "pointer",
                            borderBottom: i < results.length - 1 ? "1px solid #f1f5f9" : "none",
                            "&:hover": { bgcolor: "rgba(37,99,235,0.06)" },
                        }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#1e293b", lineHeight: 1.3 }}>
                                {r.label}
                                {r.isTab && (
                                    <Box component="span" sx={{
                                        ml: 0.6, px: 0.5, py: 0.1, fontSize: 10, fontWeight: 700,
                                        color: "#4f46e5", bgcolor: "#eef2ff", borderRadius: 0.5,
                                    }}>탭</Box>
                                )}
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: "#94a3b8", lineHeight: 1.3 }}>
                                {r.crumbs.slice(-2).join(" › ")}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}
            {q.trim() && results.length === 0 && (
                <Typography sx={{ mt: 0.5, px: 0.5, fontSize: 11.5, color: "#cbd5e1" }}>
                    일치하는 메뉴가 없습니다
                </Typography>
            )}
        </Box>
    );
}

/* ─── 안 읽음 뱃지 ──────────────────────────────────
   0·undefined 면 아무것도 렌더하지 않는다 — "(0)" 이 붙어 있으면 사용자가 매번 0인지 확인하게 되고,
   뱃지의 본래 역할(눈에 띄어야 할 때만 띈다)이 죽는다.
   색은 메일함 안쪽 폴더 뱃지와 같은 파랑(#2563eb) — 같은 숫자가 화면을 옮길 때마다 빨강↔파랑으로
   바뀌면 «다른 것»으로 읽힌다. 빨강은 경고에 남겨 둔다. */
function UnreadBadge({ count, dense = false }) {
    if (!count) return null;
    return (
        <Box component="span" sx={{
            ml: 0.6, flexShrink: 0,
            minWidth: dense ? 16 : 18, height: dense ? 16 : 18, px: 0.45,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            fontSize: dense ? 10 : 10.5, fontWeight: 700, lineHeight: 1,
            color: "#fff", bgcolor: "#2563eb", borderRadius: 99,
        }}>
            {count > 99 ? "99+" : count}
        </Box>
    );
}

/* ─── 1depth: 섹션 헤더 (아이콘 + 라벨 + 화살표) ─── */
function SectionRow({ node, open, expanded, onToggle, pathname, search, badge = 0 }) {
    const isLink  = !!node.to;
    const active  = isLink && matched(node.to, pathname, node.exact, search);

    const inner = (
        <Box onClick={!isLink ? onToggle : undefined} sx={{
            display: "flex", alignItems: "center",
            px: open ? 1.5 : 0, py: open ? 0.75 : 0,
            mx: open ? 0.75 : 0,
            borderRadius: 0,
            cursor: "pointer",
            position: "relative",
            bgcolor: active ? "rgba(37,99,235,0.07)" : "transparent",
            "&:hover": { bgcolor: active ? "rgba(37,99,235,0.07)" : "rgba(0,0,0,0.04)" },
            transition: "background 0.12s",
            justifyContent: open ? "flex-start" : "center",
            height: open ? "auto" : 44,
        }}>
            {/* 아이콘 */}
            <Box sx={{
                display: "flex", alignItems: "center", justifyContent: "center",
                width: open ? 28 : 36, height: open ? 28 : 36,
                borderRadius: open ? 1 : 1.5,
                color: active ? "#2563eb" : "#475569",
                bgcolor: open ? "transparent" : (active ? "rgba(37,99,235,0.1)" : "transparent"),
                flexShrink: 0,
                "&:hover": { bgcolor: open ? "transparent" : "rgba(0,0,0,0.06)" },
                position: "relative",
            }}>
                {node.icon}
                {/* 접힌 사이드바엔 숫자를 넣을 자리가 없다 — 점만 찍어 "뭔가 있다"는 신호만 남긴다. */}
                {!open && badge > 0 && (
                    <Box sx={{
                        position: "absolute", top: 6, right: 6,
                        width: 7, height: 7, borderRadius: 99,
                        bgcolor: "#2563eb", boxShadow: "0 0 0 1.5px #fff",
                    }} />
                )}
            </Box>
            {/* 라벨 + 뱃지 + 화살표 */}
            {open && (
                <>
                    <Typography sx={{
                        flex: 1, ml: 1,
                        // 활성 항목만 볼드 — 전부 700이면 선택 위계가 죽는다
                        fontSize: 14.5, fontWeight: active ? 700 : 600,
                        color: active ? "#1d4ed8" : "#1e293b",
                        lineHeight: 1.4,
                    }}>
                        {node.label}
                    </Typography>
                    <UnreadBadge count={badge} />
                    {!isLink && node.children?.length > 0 && (
                        expanded
                            ? <ExpandLessRoundedIcon sx={{ fontSize: 15, color: "#94a3b8" }} />
                            : <ExpandMoreRoundedIcon sx={{ fontSize: 15, color: "#94a3b8" }} />
                    )}
                </>
            )}
        </Box>
    );

    if (isLink) {
        if (node.external) {
            return (
                <Tooltip title={!open ? node.label : ""} placement="right">
                    <Box component="a" href={node.to} target="_blank" rel="noopener noreferrer" sx={{ textDecoration: "none", display: "block" }}>
                        {inner}
                    </Box>
                </Tooltip>
            );
        }
        return (
            <Tooltip title={!open ? node.label : ""} placement="right">
                <Box component={RouterLink} to={node.to} sx={{ textDecoration: "none", display: "block" }}>
                    {inner}
                </Box>
            </Tooltip>
        );
    }
    return (
        <Tooltip title={!open ? node.label : ""} placement="right">
            <Box>{inner}</Box>
        </Tooltip>
    );
}

/* ─── 2depth+: 서브그룹 — 좌측 트리 라인 아래 일자 나열 ──
   재무/인사처럼 "링크이면서 자식(탭)도 있는" 섹션을 지원한다:
   - 섹션(자금관리 등): 클릭 = 이동(링크) + 활성 섹션이면 자식 탭을 자동 펼침(연동).
   - 활성 리프(탭): 우측 화살표(>). 자식 있는 항목: 펼침/접힘 토글(∨/∧).
   하이라이트는 "현재 선택된 그 항목"에만 — 부모 섹션은 자식 탭이 활성이면 스스로 파래지지 않는다. */
function SubGroup({ node, depth, pathname, search, badges }) {
    const isLink = !!node.to;
    const hasChildren = !!node.children?.length;
    // 자식(탭) 중 실제 활성이 있으면 부모 섹션은 하이라이트 제외 → 현재 선택한 탭만 파랗게.
    const descActive = anyDescendantMatched(node, pathname, search);
    const selfActive = isLink && !descActive && matched(node.to, pathname, node.exact, search);
    const isLeaf = isLink && !hasChildren;

    // 활성(현재 위치)이 되면 자동으로 펼치되, 사용자가 수동으로 접는 것도 허용.
    const [expOpen, setExpOpen] = useState(selfActive || descActive);
    useEffect(() => {
        if (selfActive || descActive) setExpOpen(true);
    }, [selfActive, descActive]);

    const toggle = (e) => { e.preventDefault(); e.stopPropagation(); setExpOpen(v => !v); };

    const row = (
        <Box onClick={!isLink && hasChildren ? () => setExpOpen(v => !v) : undefined} sx={{
            display: "flex", alignItems: "center",
            pl: 1.25, pr: 1, py: 0.6,
            cursor: "pointer",
            borderRadius: 0, mr: 0.75,
            bgcolor: selfActive ? "rgba(37,99,235,0.07)" : "transparent",
            "&:hover": { bgcolor: selfActive ? "rgba(37,99,235,0.07)" : "rgba(0,0,0,0.04)" },
            transition: "background 0.12s",
        }}>
            <Typography sx={{
                flex: 1,
                // 450 은 static 폰트 로드 시 400 으로 반올림됨 — 중간 굵기는 500 으로 명시
                fontSize: 14, fontWeight: selfActive ? 700 : 500,
                color: selfActive ? "#1d4ed8" : "#475569",
                lineHeight: 1.35,
            }}> {node.label}
            </Typography>
            <UnreadBadge count={badges?.[node.id]} dense />
            {/* 활성 리프(탭) — 우측 화살표 */}
            {isLeaf && selfActive && (
                <ChevronRightRoundedIcon sx={{ fontSize: 15, color: "#2563eb" }} />
            )}
            {/* 자식 있는 항목 — 펼침/접힘 토글 (링크여도 이 클릭은 이동 대신 토글) */}
            {hasChildren && (
                <Box component="span" onClick={toggle} sx={{ display: "inline-flex", cursor: "pointer" }}>
                    {expOpen
                        ? <ExpandLessRoundedIcon sx={{ fontSize: 13, color: "#94a3b8" }} />
                        : <ExpandMoreRoundedIcon sx={{ fontSize: 13, color: "#94a3b8" }} />}
                </Box>
            )}
        </Box>
    );

    return (
        <Box>
            {isLink
                ? node.external
                    ? <Box component="a" href={node.to} target="_blank" rel="noopener noreferrer" sx={{ textDecoration: "none", display: "block" }}>{row}</Box>
                    : <Box component={RouterLink} to={node.to} sx={{ textDecoration: "none", display: "block" }}>{row}</Box>
                : row}
            {hasChildren && (
                <Collapse in={expOpen} timeout={160} unmountOnExit>
                    <SubTreeLine inset="10px">
                        {node.children.map(child => (
                            <SubGroup key={child.id} node={child} depth={depth + 1} pathname={pathname} search={search} badges={badges} />
                        ))}
                    </SubTreeLine>
                </Collapse>
            )}
        </Box>
    );
}

/* ─── 전체 트리 ─────────────────────────────────── */
function MenuTree({ nodes, pathname, search, expandedMap, toggleExpand, onAction, badges }) {
    const history = useHistory();

    // 펼치기와 «처음으로»를 함께 처리한다. resetTo 가 있는 항목은 이름을 누를 때마다
    // 그 화면으로 다시 보내 초기 상태로 되돌린다(이미 그 경로여도 push 해서 화면이 반응하게 한다).
    const handleToggle = (node) => {
        toggleExpand(node.id);
        // state 로 «리셋해 달라» 를 명시한다. location.key 만 보고 판단하면 화면이 스스로 부르는
        // history.replace(딥링크 파라미터 제거 등)까지 리셋으로 오해해 방금 연 상세가 닫힌다.
        if (node.resetTo) history.push(node.resetTo, { resetView: Date.now() });
    };

    return (
        <Stack spacing={0.15} px={0.5}>
            {nodes.map(node => {
                if (node.type === "groupLabel") {
                    const firstGroup = node.id === "project_group";
                    return (
                        <Box key={node.id} sx={{ px: 1.5, pt: 1.4, pb: 0.45 }}>
                            {!firstGroup && <Box sx={{ height: 1, bgcolor: "rgba(15,23,42,0.08)", mb: 0.75 }} />}
                            <Typography sx={{
                                px: 0.5,
                                fontSize: 12.5,
                                fontWeight: 700, // 작은 한글에 800 은 획이 뭉개짐
                                color: "#94a3b8",
                                letterSpacing: "0.02em",
                            }}>
                                {node.label}
                            </Typography>
                        </Box>
                    );
                }
                const isLink = !!node.to;
                const exp = !!expandedMap[node.id];
                // 액션 항목(to·children 없음)은 펼치는 대신 핸들러를 부른다.
                const isAction = node.type === "action";
                return (
                    <Box key={node.id}>
                        <SectionRow
                            node={node} open
                            expanded={exp}
                            onToggle={isAction ? () => onAction?.(node.id) : () => handleToggle(node)}
                            pathname={pathname}
                            search={search}
                            badge={badges?.[node.id]}
                        />
                        {!isLink && node.children?.length > 0 && (
                            <Collapse in={exp} timeout={180} unmountOnExit>
                                <Box pb={0.5}>
                                    <SubTreeLine>
                                        {node.children.map(child => (
                                            <SubGroup key={child.id} node={child} depth={1} pathname={pathname} search={search} badges={badges} />
                                        ))}
                                    </SubTreeLine>
                                </Box>
                            </Collapse>
                        )}
                    </Box>
                );
            })}
        </Stack>
    );
}

/* ─── 이름 이니셜 추출 ──────────────────────────── */
function getInitials(name) {
    if (!name) return "?";
    return name.trim().slice(-2);
}

/* ─── 메인 컴포넌트 ─────────────────────────────── */
export default function OnandmeSidebar({ open, onToggle, isAuthenticated, isAdmin, onLogout }) {
    const location = useLocation();
    const history  = useHistory();
    const pathname = location.pathname || "/";
    const search = location.search || "";

    // 내 정보 — 아래 menuTree 가 me?.email 로 담당자 전용 메뉴를 가리므로 여기서 먼저 선언한다.
    // (state 를 로딩 useEffect 옆에 두면 menuTree 가 선언 전 참조가 되어 TDZ 로 터진다)
    const [me, setMe] = useState(null);

    const [myPermissions, setMyPermissions] = useState(null); // null = 아직 로딩 전
    // verifyAdmin API 의 isAdmin prop 외에, me/permissions 응답에 GLOBAL_ADMIN 이 있어도 관리자로 간주.
    // 두 API 응답이 어긋날 때(예: 캐시 만료/타이밍 차이)에도 일관되게 메뉴가 보이도록.
    const effectiveAdmin = useMemo(() => {
        if (isAdmin) return true;
        if (!Array.isArray(myPermissions)) return false;
        return myPermissions.map(normalizePermCode).includes("GLOBAL_ADMIN");
    }, [isAdmin, myPermissions]);
    const [departments, setDepartments] = useState([]);   // 지출요청 하위 부서 항목용
    const menuTree = useMemo(() => buildMenu(effectiveAdmin, myPermissions, departments), [effectiveAdmin, myPermissions, departments]);

    const [expandedMap, setExpandedMap] = useState({
        event: false, kv: false, d3: false, venue: false, led: false,
        proposal: false, approval: false, finance: false, hr: false,
    });

    const toggleExpand = useCallback((id) => {
        setExpandedMap(prev => ({ ...prev, [id]: !prev[id] }));
    }, []);

    /* 경로 기반 자동 펼치기 */
    useEffect(() => {
        const p = pathname;
        const patch = {};
        if (p.startsWith("/onandme/list/2d")     || p.startsWith("/onandme/upload/2d"))    { patch.event = true; patch.kv    = true; }
        if (p.startsWith("/onandme/list/3d")     || p.startsWith("/onandme/upload/3d"))    { patch.event = true; patch.d3    = true; }
        if (p.startsWith("/onandme/event-venue") || p.startsWith("/onandme/upload/event-venue")) { patch.event = true; patch.venue = true; }
        if (p.startsWith("/qr-event-admin-list") || p.startsWith("/qr-event-participant-attendance")) patch.led = true;
        // 제안서 메뉴(children 보유) 자동 펼치기. 나라장터(/g2b) 는 단독 링크라 토글 불필요.
        if (p.startsWith("/onandme/proposal")) patch.proposal = true;
        if (p.startsWith("/employees") || p.startsWith("/me")) patch.staff = true;
        // 재무·인사·결재·메일 진입 시 상위 메뉴 자동 펼침 → 하위 항목이 바로 보이게
        if (p.startsWith("/finance")) patch.finance = true;
        if (p.startsWith("/hr")) patch.hr = true;
        if (p.startsWith("/approval")) patch.approval = true;
        if (p.startsWith("/mail")) patch.mail = true;
        // calendar는 최상위 링크라 별도 expandedMap 불필요
        if (Object.keys(patch).length) setExpandedMap(prev => ({ ...prev, ...patch }));
    }, [pathname]);

    /* 내 정보 + 내 권한 — state 는 위(menuTree 앞)에서 선언했다 */
    useEffect(() => {
        if (!isAuthenticated) return;
        Promise.all([
            api.get(`${API_BASE}/employees/me`).catch(() => null),
            api.get(`${API_BASE}/employees/me/permissions`).catch(() => []),
            api.get(`${API_BASE}/employees/departments`).catch(() => []),
        ]).then(([meData, perms, depts]) => {
            if (meData) setMe(meData);
            setMyPermissions(perms || []);
            setDepartments(Array.isArray(depts) ? depts : (depts?.departments ?? []));
        }).catch(() => { setMyPermissions([]); });
    }, [isAuthenticated]);

    /* 비밀번호 변경 모달 — 사이드바 'change_pw' 액션 항목에서 연다. */
    const [pwOpen, setPwOpen] = useState(false);
    const handleMenuAction = useCallback((id) => {
        if (id === "change_pw") setPwOpen(true);
    }, []);

    /* 재무 진입 때마다 부서 목록 새로고침 —
       부서 추가·이름변경·삭제가 사이드바 '지출요청-{부서}' 항목에 바로 반영되도록.
       pathname 은 /finance 안에서 탭·부서를 바꿔도 그대로라, 재무에 '들어올 때'만 재조회된다. */
    const onFinance = pathname.startsWith("/finance");
    useEffect(() => {
        if (!isAuthenticated || !onFinance) return;
        api.get(`${API_BASE}/employees/departments`)
            .then(depts => setDepartments(Array.isArray(depts) ? depts : (depts?.departments ?? [])))
            .catch(() => {});
    }, [isAuthenticated, onFinance]);

    /* ─── 메일 안 읽음 뱃지 ──────────────────────────────────────────────
       setInterval 로 짧게 돌리지 않는다 — 사이드바는 모든 화면에 상주하므로
       주기 폴링을 걸면 접속자 수만큼 요청이 곱해진다. 갱신 시점은 «사람이 화면을
       다시 볼 때»로 한정: 로그인 직후 · 탭 포커스 복귀 · 메일함에서 빠져나올 때.
       그마저도 포커스 연타(창 전환 반복)를 대비해 30초 throttle 을 건다. */
    const [mailUnread, setMailUnread] = useState({});
    const mailUnreadAtRef = useRef(0);

    const loadMailUnread = useCallback(async (force = false) => {
        if (!force && Date.now() - mailUnreadAtRef.current < 30_000) return;
        mailUnreadAtRef.current = Date.now();
        try {
            const res = await mailApi.unreadCounts();
            const d = res?.data ?? res ?? {};
            // MailUnreadCountDto 는 폴더맵을 {counts:{INBOX:3,...}} 로 한 겹 감싼다(MailPage 와 동일한 함정).
            setMailUnread(d?.counts ?? d ?? {});
        } catch { /* 뱃지는 부가 정보 — 실패해도 화면을 건드리지 않고 직전 값을 유지 */ }
    }, []);

    useEffect(() => {
        if (!isAuthenticated) { setMailUnread({}); return; }
        loadMailUnread(true);
        const onWake = () => { if (document.visibilityState !== "hidden") loadMailUnread(); };
        // 메일함이 자기 뱃지를 갱신할 때마다(새로고침·읽음처리·삭제·이동) 같은 숫자를 그대로 넘겨받는다.
        // 사이드바가 따로 재조회하지 않으므로 요청은 늘지 않고, 두 화면의 숫자가 어긋날 일도 없다.
        const onMailCounts = (e) => {
            const counts = e?.detail;
            if (!counts) return;
            mailUnreadAtRef.current = Date.now(); // 방금 받은 값이 최신 — 직후 포커스 복귀에 재조회 안 함
            setMailUnread(counts);
        };
        window.addEventListener("focus", onWake);
        document.addEventListener("visibilitychange", onWake);
        window.addEventListener("mail:unread", onMailCounts);
        return () => {
            window.removeEventListener("focus", onWake);
            document.removeEventListener("visibilitychange", onWake);
            window.removeEventListener("mail:unread", onMailCounts);
        };
    }, [isAuthenticated, loadMailUnread]);

    /* 메일함을 떠나는 순간은 방금 읽음 처리가 끝난 시점 — 여기만 throttle 을 건너뛴다.
       (안 그러면 다 읽고 나왔는데 뱃지가 최대 30초 동안 남아 "안 지워진다" 로 보인다) */
    const onMail = pathname.startsWith("/mail");
    const wasOnMailRef = useRef(onMail);
    useEffect(() => {
        if (wasOnMailRef.current && !onMail && isAuthenticated) loadMailUnread(true);
        wasOnMailRef.current = onMail;
    }, [onMail, isAuthenticated, loadMailUnread]);

    /* 메뉴 id → 뱃지 숫자. '받은 메일함' 하나만 — 상위 '메일' 에는 달지 않는다.
       부모·자식이 같은 숫자를 나란히 달면 «둘이 다른 것인가» 를 매번 확인하게 되고,
       그런 뱃지가 메뉴마다 늘면 사이드바 전체가 시끄러워진다. 알림은 한 곳에서만. */
    const inboxUnread = Number(mailUnread?.INBOX) || 0;
    const menuBadges = useMemo(() => ({ mail_inbox: inboxUnread }), [inboxUnread]);

    /* 접힌 레일(아이콘만)에서는 '받은 메일함' 행 자체가 없다 — 숫자 대신 '메일' 아이콘 위 점으로만 알린다.
       (SectionRow 가 open=false 일 때 숫자를 그리지 않고 7px 점만 찍는다) */
    const railBadges = useMemo(() => ({ mail: inboxUnread }), [inboxUnread]);

    /* 세션 타이머 */
    const [timeLeft, setTimeLeft] = useState(null);
    useEffect(() => {
        let tid;
        // 0 이 됐을 때의 로그아웃은 App 이 맡는다(사이드바가 없는 화면에서도 만료돼야 하므로).
        // 여기는 표시 전용 — 다만 exp 가 아직 없을 때도 <b>타이머를 멈추지 않는다</b>.
        // 예전엔 여기서 return 해 버려, 마운트 시점에 exp 가 없으면 타이머가 영영 죽어 있었다.
        const tick = () => {
            const exp = Number(localStorage.getItem("auth_token_exp"));
            setTimeLeft(exp ? Math.max(0, exp - Date.now()) : null);
            tid = setTimeout(tick, 1000);
        };
        tick();
        return () => clearTimeout(tid);
    }, []);

    const timerLabel = useMemo(() => {
        if (timeLeft == null) return null;
        const s = Math.floor(timeLeft / 1000);
        return `${Math.floor(s / 60)}분 ${s % 60}초`;
    }, [timeLeft]);

    const handleExtend = async () => {
        if (!(await confirmDialog({ message: "로그인 시간을 연장하시겠습니까?" }))) return;
        // 연장 자체는 util/session 이 한다 — 만료 임박 안내(App)와 같은 동작·같은 문구여야 한다.
        // 화면의 남은 시간은 아래 1초 tick 이 localStorage 를 다시 읽어 알아서 따라온다.
        await extendSession();
    };

    const w = open ? OPEN_W : CLOSED_W;

    return (
        <Box sx={{
            position: "fixed", top: 0, left: 0, height: "100vh",
            width: w, transition: "width 220ms cubic-bezier(.2,.8,.2,1)",
            overflow: "hidden", zIndex: theme => theme.zIndex.drawer,
            display: "flex", flexDirection: "column",
            bgcolor: "#fff",
            borderRight: "1px solid rgba(0,0,0,0.07)",
            boxShadow: "1px 0 0 rgba(0,0,0,0.04)",
        }}>

            {/* 헤더 */}
            <Box sx={{
                px: open ? 2 : 1, py: 1.2,
                display: "flex", alignItems: "center",
                justifyContent: open ? "space-between" : "center",
                borderBottom: "1px solid rgba(0,0,0,0.06)",
                gap: 1, flexShrink: 0, minHeight: 52,
            }}>
                {open ? (
                    <Box component={RouterLink} to="/"
                        sx={{ display: "inline-flex", alignItems: "center", textDecoration: "none" }}>
                        <Box component="img" src="/brand/logo.png" alt="ONANDME" sx={{ height: 22 }} />
                    </Box>
                ) : (
                    <Box sx={{ height: 22 }} />
                )}

                {open && isAuthenticated && timerLabel && (
                    <Tooltip title="클릭 시 세션 연장" arrow>
                        <Link component="button" onClick={handleExtend} underline="hover"
                            sx={{ fontSize: 11, fontWeight: 600, color: "#94a3b8", cursor: "pointer", whiteSpace: "nowrap" }}>
                            {timerLabel}
                        </Link>
                    </Tooltip>
                )}

                {/* 회색 원 배경은 뺐다 — 이 버튼은 로고·세션시간과 한 줄에 있어 배경이 있으면 저 혼자
                    «눌러야 할 것» 처럼 튄다. 접기는 상시 노출되는 보조 동작이라 조용해야 한다.
                    대신 hover 에서 색만 바뀐다(세부 사이드바 토글과 같은 규칙). */}
                <Tooltip title={open ? "접기" : "펼치기"} placement="right" arrow>
                    <IconButton onClick={onToggle} size="small"
                        aria-expanded={open}
                        aria-label={open ? "사이드바 접기" : "사이드바 펼치기"}
                        sx={{ p: 0.3, color: "#64748b", "&:hover": { color: "#2563eb", bgcolor: "transparent" } }}>
                        {open
                            ? <ChevronLeftRoundedIcon sx={{ fontSize: 17 }} />
                            : <ChevronRightRoundedIcon sx={{ fontSize: 17 }} />}
                    </IconButton>
                </Tooltip>
            </Box>

            {/*
              사용자 프로필 — 카드 자체가 클릭 가능. 클릭하면 대시보드(/) 로 이동.
              과거에 있던 "내 정보" 버튼은 제거 — 내 정보는 이제 대시보드의 좌측 카드로 흡수됐고
              비밀번호 변경은 대시보드 안의 모달에서 처리한다.
            */}
            {open && isAuthenticated && (
                <Box
                    onClick={() => history.push("/")}
                    sx={{
                        display: "flex", alignItems: "center", gap: 1.5,
                        px: 2, py: 1.5, cursor: "pointer",
                        borderBottom: "1px solid rgba(0,0,0,0.06)",
                        flexShrink: 0,
                        "&:hover": { bgcolor: "rgba(37,99,235,0.04)" },
                    }}>
                    <Avatar sx={{
                        width: 36, height: 36,
                        bgcolor: "#2563eb",
                        fontSize: 13, fontWeight: 700,
                        flexShrink: 0,
                        boxShadow: "0 0 0 2px rgba(37,99,235,0.18)",
                    }}>
                        {getInitials(me?.name)}
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{
                            fontSize: 14, fontWeight: 700,
                            color: "#1e293b",
                            lineHeight: 1.4,
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        }}>
                            {me?.name || "—"}
                        </Typography>
                        <Typography sx={{
                            fontSize: 11, fontWeight: 500,
                            color: "#94a3b8",
                            lineHeight: 1.4,
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        }}>
                            {[me?.deptName, me?.positionName].filter(Boolean).join(" · ") || "소속 없음"}
                        </Typography>
                    </Box>
                    {/* 알람 종 — 프로필 카드 클릭(대시보드 이동)과 충돌하지 않도록 AppHeader 내부에서 stopPropagation 처리. */}
                    <AppHeader />
                </Box>
            )}

            {/* 통합 검색 — 대분류·중분류·세분류·소분류 + 페이지 탭까지 검색해 바로 이동 */}
            {open && isAuthenticated && (
                <SidebarSearch menuTree={menuTree} history={history} />
            )}

            {/* 메뉴 */}
            <Box sx={{
                flex: 1, overflowY: "auto", py: 1,
                "&::-webkit-scrollbar": { width: 4 },
                "&::-webkit-scrollbar-thumb": { bgcolor: "rgba(0,0,0,0.1)", borderRadius: 99 },
            }}>
                {open ? (
                    <MenuTree
                        nodes={menuTree}
                        pathname={pathname}
                        search={search}
                        expandedMap={expandedMap}
                        toggleExpand={toggleExpand}
                        onAction={handleMenuAction}
                        badges={menuBadges}
                    />
                ) : (
                    /* 접힌 상태: 아이콘만 */
                    <Stack spacing={0.5} alignItems="center" pt={0.5}>
                        {menuTree.map(node => (
                            node.type === "groupLabel"
                                ? <Box key={node.id} sx={{ width: 28, height: 1, bgcolor: "rgba(15,23,42,0.1)", my: 0.4 }} />
                                : <SectionRow
                                    key={node.id}
                                    node={node}
                                    open={false}
                                    expanded={false}
                                    // 접힌 상태에서 액션 항목은 사이드바를 펴는 대신 바로 실행한다.
                                    onToggle={node.type === "action"
                                        ? () => handleMenuAction(node.id)
                                        : () => { onToggle(); }}
                                    pathname={pathname}
                                    search={search}
                                    badge={railBadges[node.id]}
                                />
                        ))}
                    </Stack>
                )}
            </Box>

            {/* ─── 하단 고정 영역 (로그아웃 + 카피라이트) ─── */}
            {/*
              로그아웃 버튼은 항상 사이드바 제일 아래에 고정.
              메뉴(스크롤 영역)와 분리돼 있어 메뉴를 끝까지 스크롤하지 않아도 항상 손에 닿게.
              접힌 상태에서는 아이콘만 노출.
            */}
            {isAuthenticated && (
                <Box sx={{
                    px: open ? 1.25 : 0.5, py: open ? 1 : 0.75,
                    flexShrink: 0,
                    display: "flex", flexDirection: "column",
                    alignItems: open ? "stretch" : "center",
                    gap: 0.5,
                }}>
                    {open ? (
                        <ActionButton
                            variant="secondary"
                            startIcon={<LogoutRoundedIcon sx={{ fontSize: 16 }} />}
                            onClick={async () => {
                                if (!(await confirmDialog({ message: "로그아웃 하시겠습니까?" }))) return;
                                try { await onLogout?.(); }
                                catch { /* App.handleLogout 이 처리 */ }
                            }}
                            sx={{
                                fontSize: 12, fontWeight: 600,
                                justifyContent: "flex-start",
                                color: "grey",
                                borderColor: "transparent",
                                px: 1.25, py: 0.55,
                                "&:hover": { borderColor: "transparent", bgcolor: "rgba(0,0,0,0.04)", color: "grey" },
                            }}
                            label="로그아웃" />
                    ) : (
                        <Tooltip title="로그아웃" placement="right" arrow>
                            <IconButton size="small"
                                onClick={async () => {
                                    if (!(await confirmDialog({ message: "로그아웃 하시겠습니까?" }))) return;
                                    try { await onLogout?.(); }
                                    catch { /* App.handleLogout 이 처리 */ }
                                }}
                                sx={{
                                    color: "#dc2626",
                                    "&:hover": { bgcolor: "rgba(220,38,38,0.08)" },
                                }}>
                                <LogoutRoundedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            )}

            {open && (
                <Box sx={{ px: 2, py: 0.75, borderTop: "1px solid rgba(0,0,0,0.06)", flexShrink: 0 }}>
                    <Typography sx={{ fontSize: 10, color: "#e2e8f0", textAlign: "center" }}>
                        © ONANDME
                    </Typography>
                </Box>
            )}

            {/* 비밀번호 변경 — 서버가 requireLogout 을 내리면 재로그인시킨다(토큰이 옛 비번 기준). */}
            <PasswordChangeModal
                open={pwOpen}
                onClose={() => setPwOpen(false)}
                onAfterChange={({ requireLogout }) => {
                    if (requireLogout) window.location.replace("/");
                }}
            />
        </Box>
    );
}
